import _ from 'underscore';

import {Task, ParameterType} from 'analyses/primers/PrimerWizardConstants';
import PRIMER_WIZARD_PARAMS, {PrimerWizardParams} from 'analyses/primers/PrimerWizardParams';
import {Sequence} from 'components/Sequence';
import {PrimerPreferences} from 'constants/PrimerConstants';
import * as sequtil from 'molbio/SeqUtil';
import * as navigation from 'Navigation';
import preferences from 'Preferences';

interface PrimerWizardParamValues {
  task: Task;
  [label: string]: string | number;
}

interface PrimerWizardTabStateAttributes {
  params: PrimerWizardParamValues;
  sequence: Sequence;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  buffers: any;
}

interface PrimerWizardTabState {
  get<K extends keyof PrimerWizardTabStateAttributes>(key: K): PrimerWizardTabStateAttributes[K];
}

class PrimerWizardTabState extends navigation.TabState {
  defaults(): navigation.TabStateDefaults {
    const pref = preferences.get(PrimerPreferences.WIZARD);
    return _(super.defaults()).extend({
      title: 'Primer Wizard',
      params: pref != null ? pref : {task: Task.PCR},
    });
  }

  getDefault(param: PrimerWizardParams, task: Task): string | number | null {
    // eslint-disable-next-line benchlint/no-in-operator
    if (!('tasks' in param)) {
      return param.default;
    }

    const taskInfo = _.findWhere(param.tasks!, {task});

    // Returns null if no default exists for specified task
    return (taskInfo != null ? taskInfo.default : undefined) || null;
  }

  savePreferences(): void {
    const paramsToSave: PrimerWizardParamValues = {task: this.get('params').task};
    const POSITION_TYPES = ['start_index', 'end_index', 'int_list', 'intervals'];
    const {task} = this.get('params');
    // eslint-disable-next-line guard-for-in, no-restricted-syntax
    for (const key in PRIMER_WIZARD_PARAMS) {
      const param = PRIMER_WIZARD_PARAMS[key];
      const {label} = param;
      const isPosition = Array.from(POSITION_TYPES).includes(param.type);
      if (
        !isPosition &&
        // eslint-disable-next-line benchlint/no-in-operator
        label in this.get('params') &&
        !(this.get('params')[label] === String(this.getDefault(param, task)))
      ) {
        paramsToSave[label] = this.get('params')[label];
      }
    }
    preferences.set(PrimerPreferences.WIZARD, paramsToSave);
  }

  isNumber(n: string): boolean {
    // eslint-disable-next-line no-restricted-globals, no-restricted-syntax
    return !isNaN(parseFloat(n)) && _.isFinite(n);
  }

  getInteger(n: string): number | null {
    if (this.isNumber(n) && parseFloat(n) % 1 === 0) {
      // oxlint-disable-next-line radix
      return parseInt(n);
    }
    return null;
  }

  getFloat(n: string): number | null {
    if (this.isNumber(n)) {
      return parseFloat(n);
    }
    return null;
  }

  getBooleanInt(n: string): number | null {
    const lower = n.toLowerCase();
    if (lower === 'true') {
      return 1;
    } else if (lower === 'false') {
      return 0;
    }
    return null;
  }

  getStartIndex(n: string): number | null {
    let index = this.getInteger(n);
    if (index != null) {
      index = sequtil.displayStartToModel(index, this.get('sequence').startIndex());
    }
    return index;
  }

  getEndIndex(n: string): number | null {
    let index = this.getInteger(n);
    if (index != null) {
      index = sequtil.displayEndToModel(
        index,
        this.get('sequence').length(),
        this.get('sequence').startIndex()
      );
    }
    return index;
  }

  getIntervals(s: string): string | null {
    const unparsedRegions = s.split(',');
    const regions = [];
    for (const region of Array.from(unparsedRegions)) {
      let [start, end]: Array<string | number | null> = Array.from(region.split('-'));
      start = this.getInteger(start);
      end = this.getInteger(end);
      if (!(start != null && end != null)) {
        return null;
      }
      const length = sequtil.distance(
        start,
        end,
        this.get('sequence').length(),
        this.get('sequence').circular()
      );
      if (length < 0) {
        return null;
      }
      start = sequtil.displayStartToModel(start, this.get('sequence').startIndex());
      regions.push(`${start},${length}`);
    }
    return regions.join(' ');
  }

  getIntList(s: string): string | null {
    const trimmed = s.trim();
    const junctions = [];
    for (let jxn of Array.from(trimmed.split(',')) as Array<string | number | null>) {
      jxn = this.getInteger(jxn as string);
      if (jxn != null) {
        const index = sequtil.displayStartToModel(jxn, this.get('sequence').startIndex());
        if (!(0 <= index && index < this.get('sequence').length())) {
          return null;
        }
        junctions.push(index);
      } else {
        return null;
      }
    }
    return junctions.join(' ');
  }

  parseAndValidate(label: string, value: string | number, type: ParameterType): string | number {
    const TYPES: {[t in ParameterType]: (s: string) => string | number | null} = _.object([
      // eslint-disable-next-line @typescript-eslint/unbound-method
      [ParameterType.NUMBER, this.getInteger],
      // eslint-disable-next-line @typescript-eslint/unbound-method
      [ParameterType.FLOAT, this.getFloat],
      // eslint-disable-next-line @typescript-eslint/unbound-method
      [ParameterType.INDEX_LIST, this.getIntList],
      [ParameterType.TEXT, (n: string) => n],
      // eslint-disable-next-line @typescript-eslint/unbound-method
      [ParameterType.BOOL, this.getBooleanInt],
      // eslint-disable-next-line @typescript-eslint/unbound-method
      [ParameterType.START_INDEX, this.getStartIndex],
      // eslint-disable-next-line @typescript-eslint/unbound-method
      [ParameterType.END_INDEX, this.getEndIndex],
      // eslint-disable-next-line @typescript-eslint/unbound-method
      [ParameterType.INTERVAL_LIST, this.getIntervals],
    ]);
    const val = TYPES[type].apply(this, [value]);
    if (val == null) {
      throw new TypeError(`Invalid parameter value for ${label}: ${value}`);
    }
    return val;
  }

  getParams(): PrimerWizardParamValues {
    const {task} = this.get('params');
    const params: PrimerWizardParamValues = {task};
    // eslint-disable-next-line guard-for-in, no-restricted-syntax
    for (const key in PRIMER_WIZARD_PARAMS) {
      const value = PRIMER_WIZARD_PARAMS[key];
      // eslint-disable-next-line benchlint/no-in-operator
      if (!('tasks' in value) || _.findWhere(value.tasks!, {task}) != null) {
        // eslint-disable-next-line benchlint/no-in-operator
        if (value.label in this.get('params')) {
          params[value.label] = this.parseAndValidate(
            value.label,
            this.get('params')[value.label],
            value.type
          );
        } else {
          const defaultValue = this.getDefault(value, this.get('params').task);
          if (defaultValue != null) {
            params[value.label] = this.parseAndValidate(value.label, defaultValue, value.type);
          }
        }
      }
    }
    return params;
  }
}

export default PrimerWizardTabState;
